import { logger } from "src/core/globals";
import { TimeFormat } from "src/gen/schema/models/auth";

export const UTC = "UTC";
export type TimeFormatValue = keyof typeof TimeFormat;

export const getUserTimeZone = (): string => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const upper = tz.toUpperCase();
    if (upper === "UTC") {
      return UTC;
    }
    return tz;
  } catch (error) {
    logger.error("Error determining user's time zone:", error);
    return UTC;
  }
};

export const getUserLocale = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().locale; // e.g., "en-US", "fr-FR"
  } catch (e) {
    logger.error(
      `${e}: Intl.DateTimeFormat not available, falling back to navigator.language.`
    );
  }
  if (navigator.language) {
    return navigator.language; // e.g., "en-US"
  }
  return "en-US";
};

/**
 * Returns the default hour cycle for a given locale.
 *
 * The hour cycle is determined by creating an Intl.DateTimeFormat object
 * with the locale and the hourCycle option set to "h12". The resolved
 * options are then inspected to determine the actual hour cycle used
 * by the locale. If the locale uses a 12-hour format (with AM/PM),
 * the function returns "12h". If the locale uses a 24-hour format,
 * the function returns "24h". If an error occurs (e.g., invalid locale),
 * the function falls back to "24h".
 *
 * @returns {string} The default hour cycle for the locale, either "12h" or "24h".
 */
export const getDefaultHourCycleForTimeZone = (
  timeZone: string = getUserTimeZone()
): TimeFormat => {
  const effectiveTimeZone = timeZone || getUserTimeZone();

  if (effectiveTimeZone === UTC) {
    return TimeFormat.TIME_FORMAT_24HR;
  }
  const locale = getUserLocale();
  try {
    const formatter = new Intl.DateTimeFormat(locale, {
      timeZone: effectiveTimeZone,
      hour: "numeric",
      hourCycle: "h12"
    });

    // Get the resolved options, which contain the actual hourCycle used by the locale
    const resolvedOptions = formatter.resolvedOptions();

    // The 'hourCycle' property indicates the default hour cycle for the locale:
    // 'h11' (0-11) and 'h12' (1-12) mean 12-hour format (with AM/PM)
    // 'h23' (0-23) and 'h24' (1-24) mean 24-hour format
    if (
      resolvedOptions.hourCycle === "h11" ||
      resolvedOptions.hourCycle === "h12"
    ) {
      return TimeFormat.TIME_FORMAT_12HR;
    }
    return TimeFormat.TIME_FORMAT_24HR;
  } catch (error) {
    logger.error(
      `Error determining hour cycle for locale '${locale}'. Falling back to 24h.`,
      error
    );
    return TimeFormat.TIME_FORMAT_24HR; // Fallback in case of invalid locale or other error
  }
};

/**
 * Returns timezone label for display (e.g., " UTC" or " (UTC)")
 * Only shows label when timezone is UTC, user enabled it, and there's a value to display
 */
interface GetUTCTimeZoneAbbreviationOptions {
  enabled?: boolean;
  hasValue?: boolean;
  withParentheses?: boolean;
}
export const getUTCTimeZoneAbbreviation = (
  timeZone: string,
  options: GetUTCTimeZoneAbbreviationOptions = {}
) => {
  const { enabled = true, hasValue = true, withParentheses = false } = options;
  if (timeZone === UTC && enabled && hasValue) {
    return withParentheses ? " (UTC)" : " UTC";
  }
  return "";
};
